(() => {
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.Shopify && window.Shopify.designMode) return;

  const SINGLE = [
    [
      'up',
      [
        '.kj-split__content',
        '.kj-ar__copy',
        '.kj-promo__copy',
        '.kj-steps__header',
        '.kj-steps__note',
        '.kj-story__content > .kj-eyebrow, .kj-story__content > .kj-heading, .kj-story__intro',
        '.kj-gallery__header',
        '.kj-enquiry__copy',
        '.kj-enquiry__panel',
        '.kj-reviews__header',
        '.kj-news__inner',
        '.kj-heading--title',
        '.kj-btns--center',
        '.heading-wrapper--collection',
        '.collection__description',
        '.page__content > *',
        '.article-content .content > *',
      ].join(','),
    ],
    ['media', '.kj-split__media, .kj-ar__media, .kj-promo__media, .kj-story__media, .kj-enquiry__media'],
    ['fade', '.kj-press, .kj-trust, .kj-highlights'],
  ];

  // Small grids reveal together when the grid enters; long grids reveal per item so lower rows animate on arrival.
  const GROUPED = [
    '.kj-products__grid',
    '.kj-tiles__grid',
    '.kj-reviews__grid',
    '.kj-ar__features',
    '.kj-promo__cards',
    '.kj-steps__grid',
    '.kj-story__thread',
    '.kj-gallery__grid',
    '.kj-faq__grid',
    '.kj-footer__menus',
  ].join(',');
  const PER_ITEM = '.collection-matrix';

  const STEP = 90;
  const MAX_STEPS = 5;

  const reveal = (el) => {
    el.classList.remove('kj-armed');
    el.addEventListener('transitionend', () => el.style.removeProperty('--kj-delay'), { once: true });
  };

  const groups = new WeakMap();

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        io.unobserve(entry.target);
        const children = groups.get(entry.target);
        if (children) children.forEach(reveal);
        else reveal(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.08 },
  );

  const belowFold = (el) => el.getBoundingClientRect().top > window.innerHeight * 0.92;

  const arm = (el, type, delay) => {
    if (el.dataset.kjReveal) return false;
    el.dataset.kjReveal = type;
    if (!belowFold(el)) return false;
    if (delay) el.style.setProperty('--kj-delay', `${delay}ms`);
    el.classList.add('kj-armed');
    return true;
  };

  const columns = (grid) => {
    const kids = grid.children;
    if (!kids.length) return 1;
    const top = kids[0].offsetTop;
    let n = 0;
    while (n < kids.length && kids[n].offsetTop === top) n++;
    return n || 1;
  };

  const armItems = (grid, items) => {
    const cols = columns(grid);
    items.forEach((item) => {
      const index = Array.prototype.indexOf.call(grid.children, item);
      if (arm(item, 'up', Math.min(index % cols, MAX_STEPS) * STEP)) io.observe(item);
    });
  };

  const scan = (root = document) => {
    for (const [type, selector] of SINGLE) {
      root.querySelectorAll(selector).forEach((el) => {
        if (el.offsetHeight && arm(el, type)) io.observe(el);
      });
    }

    root.querySelectorAll(GROUPED).forEach((grid) => {
      if (grid.dataset.kjGroup || !belowFold(grid)) return;
      grid.dataset.kjGroup = '';
      const armed = [...grid.children].filter((child, i) => arm(child, 'up', Math.min(i, MAX_STEPS) * STEP));
      if (!armed.length) return;
      groups.set(grid, armed);
      io.observe(grid);
    });

    root.querySelectorAll(PER_ITEM).forEach((grid) => {
      armItems(grid, [...grid.children]);
      if (grid.dataset.kjWatch) return;
      grid.dataset.kjWatch = '';
      new MutationObserver((records) => {
        const added = records.flatMap((r) => [...r.addedNodes]).filter((n) => n.nodeType === 1);
        if (added.length) armItems(grid, added);
      }).observe(grid, { childList: true });
    });
  };

  // Smooth in-page jumps for KJ links only; a global scroll-behavior would also animate Flex's scroll-lock restores.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.kj a[href^="#"]');
    if (!link || event.defaultPrevented) return;
    const target = link.hash.length > 1 && document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', link.hash);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan());
  else scan();
})();
